import apiClient from "./client.js";

/**
 * Enqueue a wizard scan for a target URL.
 * @param {string} url
 * @returns {Promise<{ job_id: string, location: string }>}
 */
export async function startWizardScan(url) {
  const res = await apiClient.post("/wizard/scan", { url });
  return res.data.data;
}

/**
 * Fetch wizard events newer than `since` for a job.
 * Returns { events, next, status }.
 *
 * @param {string} jobId
 * @param {number} since
 */
export async function fetchWizardEvents(jobId, since = 0) {
  const res = await apiClient.get(`/wizard/${jobId}/events`, {
    params: { since },
  });
  return res.data.data;
}

/**
 * Poll wizard events until the run completes or the caller aborts.
 *
 * Calls onEvent for each new event, onDone({status}) when the server reports
 * a terminal status, and onError(err) on a non-recoverable network failure.
 * Returns an object with `.stop()` to cancel.
 *
 * @param {string} jobId
 * @param {(event: {phase: string, status: string, data: object}) => void} onEvent
 * @param {(final: {status: string}) => void} [onDone]
 * @param {(err: Error) => void} [onError]
 * @param {{ intervalMs?: number }} [opts]
 */
export function pollWizardEvents(jobId, onEvent, onDone, onError, opts = {}) {
  const intervalMs = opts.intervalMs ?? 5000;
  let cancelled = false;
  let since = 0;
  let consecutiveErrors = 0;

  async function tick() {
    if (cancelled) return;
    try {
      const { events, next, status } = await fetchWizardEvents(jobId, since);
      consecutiveErrors = 0;
      since = typeof next === "number" ? next : since;
      for (const evt of events || []) {
        if (cancelled) return;
        onEvent(evt);
      }
      if (status === "completed" || status === "failed") {
        onDone?.({ status });
        return;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= 5) {
        onError?.(err);
        return;
      }
    }
    if (!cancelled) setTimeout(tick, intervalMs);
  }

  // Kick off the first poll immediately so the loading screen has signal fast.
  tick();

  return {
    stop() {
      cancelled = true;
    },
  };
}

/**
 * Fetch the final scan state including suggestions.
 * @param {string} jobId
 */
export async function getWizardScan(jobId) {
  const res = await apiClient.get(`/wizard/${jobId}`);
  return res.data.data;
}

/**
 * Create monitors from selected suggestions.
 * @param {string} jobId
 * @param {number[]} selected indexes into the suggestions array
 * @param {string[]} [locations] location slugs to run from
 */
export async function applyWizardSuggestions(jobId, selected, locations) {
  const body = { selected };
  if (locations && locations.length) body.locations = locations;
  const res = await apiClient.post(`/wizard/${jobId}/apply`, body);
  return res.data.data;
}
